// src/pages/Competitions/useCompetitionsListing.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAllCompetitions } from "./../../services/competitionsServices";
import { getUserById } from "./../../services/userServices";
import { getCompetitionCompetiteursStatistics } from "./../../services/competiteursServices";
import { countCompetitionDistributions } from "./../../services/distributionsServices";
import { countCompetitionCombats } from "./../../services/combatsServices";

import {
  buildCompetitionsCSV,
  downloadCSV,
  getOrganizerLabel,
  normalizeText,
  safeDisciplines,
  toDateOnly,
  todayDateOnly,
} from "./competitionsListingUtils";

export function useCompetitionsListing() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [organizerMap, setOrganizerMap] = useState({});
  const [statsMap, setStatsMap] = useState({});

  const [selectedCompetition, setSelectedCompetition] = useState(null);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyPublic, setOnlyPublic] = useState(false);
  const [sort, setSort] = useState("date_desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [organizerFilter, setOrganizerFilter] = useState("all");

  const loadOrganizersForCompetitions = useCallback(
    async (rows = []) => {
      try {
        const ids = [
          ...new Set(
            rows
              .map((competition) => competition?.organisateur_competition)
              .filter((id) => id !== null && id !== undefined),
          ),
        ];

        const missingIds = ids.filter((id) => organizerMap[id] === undefined);

        if (!missingIds.length) return;

        const results = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const user = await getUserById(id);
              return [id, user || null];
            } catch {
              return [id, null];
            }
          }),
        );

        setOrganizerMap((prev) => ({
          ...prev,
          ...Object.fromEntries(results),
        }));
      } catch (error) {
        console.error("Erreur chargement organisateurs :", error);
      }
    },
    [organizerMap],
  );

  const loadStatsForCompetitions = useCallback(
    async (rows = []) => {
      try {
        const ids = rows.map((competition) => competition.id);
        const missingIds = ids.filter((id) => statsMap[id] === undefined);

        if (!missingIds.length) return;

        const results = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const [competiteursStats, distributionsRes, combatsRes] =
                await Promise.all([
                  getCompetitionCompetiteursStatistics(id),
                  countCompetitionDistributions(id),
                  countCompetitionCombats(id),
                ]);

              return [
                id,
                {
                  totalInscrits: Number(competiteursStats?.total_inscrits || 0),
                  totalPresents: Number(competiteursStats?.total_presents || 0),
                  distCount: Number(distributionsRes?.count || 0),
                  combatCount: Number(combatsRes?.count || 0),
                },
              ];
            } catch (error) {
              console.error("Erreur chargement stats compétition :", id, error);

              return [
                id,
                {
                  totalInscrits: 0,
                  totalPresents: 0,
                  distCount: 0,
                  combatCount: 0,
                },
              ];
            }
          }),
        );

        setStatsMap((prev) => ({
          ...prev,
          ...Object.fromEntries(results),
        }));
      } catch (error) {
        console.error("Erreur chargement statistiques compétitions :", error);
      }
    },
    [statsMap],
  );

  const loadCompetitions = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllCompetitions();
      const rows = Array.isArray(data) ? data : [];

      setCompetitions(rows);
      setStatsMap({});

      await loadOrganizersForCompetitions(rows);
    } catch (error) {
      console.error("Erreur chargement compétitions :", error);
      toast.error("Erreur lors du chargement des compétitions.");
    } finally {
      setLoading(false);
    }
  }, [loadOrganizersForCompetitions]);

  useEffect(() => {
    loadCompetitions();
  }, [loadCompetitions]);

  const filteredCompetitions = useMemo(() => {
    const today = todayDateOnly();
    const search = normalizeText(q.trim());

    let rows = [...competitions];

    if (search) {
      rows = rows.filter((competition) => {
        const disciplines = safeDisciplines(
          competition.disciplines_competition,
        ).join(" ");

        const organizer = organizerMap[competition.organisateur_competition];
        const organizerLabel = getOrganizerLabel(
          organizer,
          competition.organisateur_competition,
        );

        const searchable = [
          competition.id,
          competition.nom_competition,
          competition.lieu_competition,
          competition.level_competition,
          disciplines,
          organizerLabel,
          organizer?.email,
        ]
          .map(normalizeText)
          .join(" ");

        return searchable.includes(search);
      });
    }

    if (onlyActive) {
      rows = rows.filter((competition) => Boolean(competition.is_active));
    }

    if (onlyPublic) {
      rows = rows.filter((competition) => Boolean(competition.is_public));
    }

    if (organizerFilter !== "all") {
      rows = rows.filter(
        (competition) =>
          String(competition.organisateur_competition) ===
          String(organizerFilter),
      );
    }

    if (status !== "all") {
      rows = rows.filter((competition) => {
        const date = toDateOnly(competition.date_competition);
        if (!date) return false;

        if (status === "past") return date < today;
        if (status === "todayup") return date >= today;
        if (status === "upcoming") return date > today;

        return true;
      });
    }

    rows.sort((a, b) => {
      if (sort === "date_desc") {
        return (
          (toDateOnly(b.date_competition)?.getTime() || 0) -
          (toDateOnly(a.date_competition)?.getTime() || 0)
        );
      }

      if (sort === "date_asc") {
        return (
          (toDateOnly(a.date_competition)?.getTime() || 0) -
          (toDateOnly(b.date_competition)?.getTime() || 0)
        );
      }

      if (sort === "name_az") {
        return String(a.nom_competition || "").localeCompare(
          String(b.nom_competition || ""),
          "fr",
        );
      }

      if (sort === "name_za") {
        return String(b.nom_competition || "").localeCompare(
          String(a.nom_competition || ""),
          "fr",
        );
      }

      return 0;
    });

    return rows;
  }, [
    competitions,
    q,
    onlyActive,
    onlyPublic,
    organizerFilter,
    status,
    sort,
    organizerMap,
  ]);

  const organizerOptions = useMemo(() => {
    const ids = [
      ...new Set(
        competitions
          .map((competition) => competition?.organisateur_competition)
          .filter((id) => id !== null && id !== undefined && id !== ""),
      ),
    ];

    const options = ids.map((id) => {
      const organizer = organizerMap[id];

      return {
        value: String(id),
        label: getOrganizerLabel(organizer, id),
      };
    });

    options.sort((a, b) => a.label.localeCompare(b.label, "fr"));

    return [
      {
        value: "all",
        label: "Tous les organisateurs",
      },
      ...options,
    ];
  }, [competitions, organizerMap]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredCompetitions.length / pageSize),
  );

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredCompetitions.slice(start, start + pageSize);
  }, [filteredCompetitions, page, pageSize]);

  useEffect(() => {
    loadOrganizersForCompetitions(pageRows);
    loadStatsForCompetitions(pageRows);
  }, [pageRows, loadOrganizersForCompetitions, loadStatsForCompetitions]);

  const stats = useMemo(() => {
    const today = todayDateOnly();

    let past = 0;
    let todayCount = 0;
    let upcoming = 0;
    let todayup = 0;
    let active = 0;
    let publicCount = 0;

    for (const competition of competitions) {
      const date = toDateOnly(competition.date_competition);

      if (competition.is_active) active += 1;
      if (competition.is_public) publicCount += 1;

      if (!date) continue;

      if (date < today) past += 1;
      if (date.getTime() === today.getTime()) todayCount += 1;
      if (date > today) upcoming += 1;
      if (date >= today) todayup += 1;
    }

    return {
      total: competitions.length,
      filtered: filteredCompetitions.length,
      past,
      today: todayCount,
      upcoming,
      todayup,
      active,
      publicCount,
    };
  }, [competitions, filteredCompetitions]);

  const exportCSV = useCallback(() => {
    const csv = buildCompetitionsCSV(filteredCompetitions, organizerMap);
    downloadCSV("competitions-monapplitournoi.csv", csv);
    toast.success("Export CSV généré.");
  }, [filteredCompetitions, organizerMap]);

  const resetFilters = useCallback(() => {
    setQ("");
    setStatus("all");
    setOnlyActive(false);
    setOnlyPublic(false);
    setSort("date_desc");
    setOrganizerFilter("all");
    setPage(1);
  }, []);

  return {
    competitions,
    filteredCompetitions,
    pageRows,
    stats,
    organizerMap,
    statsMap,
    organizerOptions,
    selectedCompetition,
    setSelectedCompetition,

    loading,

    q,
    setQ,
    status,
    setStatus,
    organizerFilter,
    setOrganizerFilter,
    onlyActive,
    setOnlyActive,
    onlyPublic,
    setOnlyPublic,
    sort,
    setSort,

    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,

    loadCompetitions,
    exportCSV,
    resetFilters,
  };
}
